import { useState, useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useGesture } from "@use-gesture/react";
import { parse } from "transform-parser";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import classNames from "classnames";
import dayjs from "dayjs";
import axios from "axios";
import { v4 as uuid } from "uuid";
import { Editor, EditorState, convertFromRaw } from "draft-js";
import { Box, Typography, CircularProgress, Skeleton, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField } from "@mui/material";
import { useTapActions } from "../hooks/hooks";
import useSymbolName from "../hooks/useSymbolName";
import { createAppbackResourceAdapter } from "../liveTap/appbackResourceAdapter";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";
import ShareModal from "../components/ShareModal";
import ZoomShareButton from "../components/ZoomShareButton";
import { usePageHeader } from "../context/PageHeaderContext";
import { useSnackbar } from "../components/AlertNotification";
import DocumentText from "../components/DocumentText";
import SlideDown from "../components/SlideDown";
import TapestryView, { getTapestryDimensions } from "../components/TapestryView";
import {
  toTitleCase,
  backgroundImageUrl,
  AUTO_TITLE_DATE_REGEX,
  titleHasDate,
  constrainTitle,
} from "../services/utilities";
import "./DetailPage.scss";
import s from "./DetailPage.module.scss";

const NUM_PHRASE_OPTIONS = 20;
const defaultMarkupHeight = 120;
const breakpoint = 1200;
const AUTO_DATE_ATTR = "data-auto-date";
const BASE_FONT_ATTR = "data-base-font-size";

// Write a new title into the tapestry SVG, hiding the auto-appended meeting date when the
// title carries a date of its own. The hidden date is parked on the tspan so it comes back
// if the user drops the date from their title later. Mirrors tapgen's setTitleText(), which
// does the same when the edit is saved and the SVG is regenerated server-side.
function setTitleText(tspan, newTitle) {
  const autoDate = tspan.getAttribute(AUTO_DATE_ATTR) || tspan.textContent.match(AUTO_TITLE_DATE_REGEX)?.[0] || "";
  const title = constrainTitle(newTitle);
  if (titleHasDate(newTitle)) {
    if (autoDate) {
      tspan.setAttribute(AUTO_DATE_ATTR, autoDate);
    }
    tspan.textContent = title.toUpperCase();
  } else {
    tspan.removeAttribute(AUTO_DATE_ATTR);
    tspan.textContent = (title + autoDate).toUpperCase();
  }
  fitTitleFontSize(tspan);
}

// The title was fitted to its layout box when the tapestry was minted: the tspan's dx is half that
// rendered width, and `.viz-text { text-anchor: middle }` keeps the text centered on its own position,
// so dx stays a faithful record of the width the title fitted into. Shrink a longer title back into
// that budget, never grow past the minted size — the same fit tapgen applies when the edit is saved.
function fitTitleFontSize(tspan) {
  const textNode = tspan.parentNode;
  const baseFontSize = +(textNode?.getAttribute(BASE_FONT_ATTR) || textNode?.getAttribute("font-size"));
  const budget = +tspan.getAttribute("dx") * 2;
  if (!baseFontSize || !budget) return;
  textNode.setAttribute("font-size", baseFontSize); // measure at the minted size, not a shrunken one
  const width = tspan.getComputedTextLength?.();
  if (!(width > 0)) return; // not laid out yet — leave the minted size alone
  textNode.setAttribute(BASE_FONT_ATTR, baseFontSize);
  if (width > budget) {
    textNode.setAttribute("font-size", baseFontSize * (budget / width));
  }
}

const DetailPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editingTapestry, setEditingTapestry] = useState(false);
  const [searchingPhrases, setSearchingPhrases] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [tapestryData, setTapestryData] = useState({});
  const [doc, setDoc] = useState({});
  const [title, setTitle] = useState();
  const [phraseOptions, setPhraseOptions] = useState();
  const [updatedPhrase, setUpdatedPhrase] = useState();
  const [tapestryHeight, setTapestryHeight] = useState("auto");
  const [redirect, setRedirect] = useState(null);
  const [markups, setMarkups] = useState([]); // all markups
  const [markupData, setMarkupData] = useState([]); // markups in the tapestry
  const [draggingMarkup, setDraggingMarkup] = useState(null);
  const [tapEdits, setTapEdits] = useState({});
  const [description, setDescription] = useState("");
  const transcriptContainerRef = useRef();
  const markupDataCache = useRef();
  const tapDataCache = useRef();
  const titleCache = useRef();

  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState("");
  const [editTitleError, setEditTitleError] = useState(null);
  const [showPanelContent, setShowPanelContent] = useState(false);
  const [lastPanelMode, setLastPanelMode] = useState(null);
  const sidePanelTimerRef = useRef();

  const { tapId } = useParams();
  const { userInfo, loggingIn } = useContext(RootContext);
  const { showSnackbar, snackbarEl } = useSnackbar();
  const { setHeaderContent } = usePageHeader();

  const resolveTapDelete = useCallback(
    (tapId) => {
      if (!tapId) {
        showSnackbar("Unable to delete Tapestry", "error");
      } else {
        setRedirect(true);
      }
    },
    [showSnackbar]
  );

  const { setDeletingTap, deleteTapConfirm } = useTapActions(resolveTapDelete);

  const deleteTap = useCallback(() => {
    setDeletingTap({ title, tapId });
  }, [setDeletingTap, tapId, title]);

  // data for editing
  const [activeLayout, setActiveLayout] = useState();
  const activeLayoutRef = useRef(activeLayout);
  const [layoutData, setLayoutData] = useState();
  const [activeBackground, setActiveBackground] = useState();
  const [backgrounds, setBackgrounds] = useState();
  // A background holds its primary symbol by symbol_id; the description panel
  // shows its name, read once the tapestry has a background.
  const catalog = useMemo(() => createAppbackResourceAdapter(), []);
  const nameOf = useSymbolName(activeBackground ? catalog : null);
  const [svg, setSvg] = useState({}); // per layout
  const [textpathOptions, setTextpathOptions] = useState({}); // per layout
  const tapestryContainer = useRef();

  // share modal
  const [modalOpen, setModalOpen] = useState(false);
  const handleModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);
  // The API names where the tap's assets live, per stage (imageUrl,
  // tapdataUrl, svgUrl); the client builds no asset URL of its own.
  const imageURI = useMemo(() => tapestryData.imageUrl ?? "", [tapestryData]);

  const searchingPhrasesRef = useRef(searchingPhrases);

  const textpathOptionsRef = useRef(textpathOptions);
  useEffect(() => {
    textpathOptionsRef.current = textpathOptions;
  }, [textpathOptions]);

  const backgroundsRef = useRef(backgrounds);
  useEffect(() => {
    backgroundsRef.current = backgrounds;
  }, [backgrounds]);

  const layoutDataRef = useRef(layoutData);
  useEffect(() => {
    layoutDataRef.current = layoutData;
  }, [layoutData]);

  const showPhraseOptions = useCallback((e) => {
    if (
      textpathOptionsRef.current[activeLayoutRef.current] &&
      textpathOptionsRef.current[activeLayoutRef.current][e.detail.eventID] &&
      e.detail.eventID &&
      !searchingPhrasesRef.current
    ) {
      setPhraseOptions(textpathOptionsRef.current[activeLayoutRef.current][e.detail.eventID]);
    }
  }, []);

  const hidePhraseOptions = useCallback(() => setPhraseOptions(null), []);

  const handleEscape = useCallback(
    (e) => {
      if (e.key === "Escape") {
        hidePhraseOptions();
      }
    },
    [hidePhraseOptions]
  );

  // hide phrase options when done editing tapestry
  useEffect(() => {
    if (!editingTapestry) {
      hidePhraseOptions();
    }
  }, [editingTapestry, hidePhraseOptions]);

  // initialize and fetch data
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const data = await AppBackService.getUserTap(userInfo.username, tapId);
      if (!data || data.error) {
        setError(true);
      } else {
        setTapestryData(data.taps);
        if (data.taps) {
          setActiveLayout(data.taps.layoutId);
          activeLayoutRef.current = data.taps.layoutId;

          // fetch tapdata
          const tapdataResp = await axios({
            method: "GET",
            url: data.taps.tapdataUrl,
          });
          if (tapdataResp.data) {
            const newTextpathOptions = { [data.taps.layoutId]: tapdataResp.data };
            setTextpathOptions(newTextpathOptions);
            tapDataCache.current = JSON.stringify(newTextpathOptions);
          }

          // fetch SVG
          const svgResp = await axios({
            method: "GET",
            url: data.taps.svgUrl,
          });
          if (svgResp.data) {
            setSvg((svg) => {
              const svgCopy = { ...svg };
              const dummyContainer = document.createElement("div");
              dummyContainer.innerHTML = svgResp.data;
              let newMarkupData = [];
              // parse out markup data and remove markups from SVG
              const markupsContainer = dummyContainer.querySelector("#tap-markups");
              if (markupsContainer && markupsContainer.children.length) {
                newMarkupData = [...markupsContainer.children]
                  .map((markupGroup) => {
                    if (!markupGroup.style.transform) return null;
                    const transform = parse(markupGroup.style.transform.trim());
                    const viewBox = markupGroup.querySelector("svg").viewBox.baseVal;
                    return {
                      id: markupGroup.id,
                      x: transform.translate[0],
                      y: transform.translate[1],
                      xScale: transform.scale[0],
                      yScale: transform.scale[1],
                      rotate: +transform.rotate.replace("rad", ""),
                      dimensions: {
                        width: viewBox.width,
                        height: viewBox.height,
                      },
                    };
                  })
                  .filter((markup) => markup);
                setMarkupData(newMarkupData);
              }
              markupDataCache.current = JSON.stringify(newMarkupData);
              markupsContainer?.remove();
              svgCopy[data.taps.layoutId] = dummyContainer.innerHTML;
              return svgCopy;
            });
          }

          // fetch document
          const doc = await AppBackService.getUserDoc(userInfo.username, data.taps.docId);
          if (doc?.docs) {
            setDoc(doc.docs[0]);
          }

          // fetch all markups
          const allMarkups = await AppBackService.getMarkups();
          if (allMarkups) {
            setMarkups(allMarkups);
          }

          setActiveBackground(data.taps.backgroundId);

          setTitle(data.taps.title);
          titleCache.current = data.taps.title;
        }

        window.addEventListener("showPhraseOptions", showPhraseOptions);
      }
      setLoading(false);
    }

    if (userInfo?.username && !loggingIn && tapId) {
      fetchData();
    }

    function updateTapestryDimensions(e) {
      setTapestryHeight(e.detail.height || "auto");
    }

    function searchText(e) {
      if (e.detail.text && searchingPhrasesRef.current) {
        const phrases = phrasesData(textpathOptionsRef.current[activeLayoutRef.current]);
        setSelectedText({ text: e.detail.text, indexes: phrases[e.detail.text] });
      }
    }

    window.addEventListener("tapestryDimensions", updateTapestryDimensions);
    window.addEventListener("selectText", searchText);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("showPhraseOptions", showPhraseOptions);
      window.removeEventListener("tapestryDimensions", updateTapestryDimensions);
      window.removeEventListener("selectText", searchText);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [handleEscape, loggingIn, showPhraseOptions, tapId, userInfo]);

  const scrollToTranscript = useCallback((condition) => {
    if (
      condition &&
      transcriptContainerRef.current &&
      transcriptContainerRef.current.offsetTop > window.innerHeight * 0.85
    ) {
      window.scrollTo({
        top: transcriptContainerRef.current.offsetTop,
        left: 0,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    searchingPhrasesRef.current = searchingPhrases;
    scrollToTranscript(searchingPhrases);
    if (!searchingPhrases) {
      setSelectedText(null);
    }
  }, [scrollToTranscript, searchingPhrases]);

  useEffect(() => {
    scrollToTranscript(selectedText?.text);
  }, [scrollToTranscript, selectedText]);

  // editing

  const tapDirty = useMemo(
    () =>
      Object.keys(tapEdits).length > 0 ||
      (title && title !== titleCache.current) ||
      JSON.stringify(textpathOptions) !== tapDataCache.current ||
      (markupDataCache.current && JSON.stringify(markupData) !== markupDataCache.current),
    [markupData, tapEdits, textpathOptions, title]
  );

  useEffect(() => {
    async function fetchBackgrounds() {
      // fetch backgrounds
      const bgs = await AppBackService.getRelatedBackgrounds(activeBackground);
      if (bgs) {
        setBackgrounds(
          bgs.reduce((obj, item) => {
            obj[item.id] = item;
            return obj;
          }, {})
        );
      }
    }
    if (!backgroundsRef.current && activeBackground) {
      fetchBackgrounds();
    }
  }, [activeBackground]);

  // fetch primary symbol description
  useEffect(() => {
    async function fetchDescription() {
      const resp = await AppBackService.getBackgroundDescription(activeBackground);
      if (resp.description) {
        setDescription(EditorState.createWithContent(convertFromRaw(JSON.parse(resp.description))));
      }
    }
    if (activeBackground) {
      fetchDescription();
    } else {
      setDescription("");
    }
  }, [activeBackground]);

  useEffect(() => {
    async function fetchLayoutData() {
      const resp = await AppBackService.getLayout(activeLayout);
      if (resp) {
        setLayoutData(resp);
      }
    }
    if (editingTapestry && !layoutDataRef.current) {
      fetchLayoutData();
    }
  }, [activeLayout, editingTapestry]);

  const changeBackground = useCallback(
    async (backgroundID) => {
      if (backgrounds && backgrounds[backgroundID]) {
        // update the SVG background
        const imageEl = tapestryContainer.current?.querySelector("image.background-image");
        if (imageEl) {
          imageEl.setAttribute("href", backgroundImageUrl(backgroundID));
        }
        setActiveBackground(backgroundID);

        // update font colors
        // update title
        const title = tapestryContainer.current?.querySelector(".tap-title tspan");
        if (title) {
          title.setAttribute(
            "fill",
            layoutData?.title?.color || backgrounds[backgroundID].colors?.primary || "#000000"
          );
        }
        // update text elements associated with specific slots from this layout
        if (layoutData?.slots) {
          for (const slot of layoutData.slots) {
            const textContainer = tapestryContainer.current?.querySelector("#phrase-" + slot.id);
            const text = textContainer?.querySelectorAll("tspan, textPath");
            if (text) {
              text.forEach((t) => {
                t.setAttribute(
                  "fill",
                  (backgrounds[backgroundID].colors &&
                    backgrounds[backgroundID].colors[slot.textpath_data?.color || "primary"]) ||
                    "#000000"
                );
              });
            }
          }
        }
        // update text elements within symbols
        const symbolTexts = tapestryContainer.current?.querySelectorAll(".tapestry-symbol textPath");
        if (symbolTexts) {
          symbolTexts.forEach((t) => {
            t.setAttribute("fill", backgrounds[backgroundID].colors?.primary || "#000000");
          });
        }

        setTapEdits((tapEdits) => ({ ...tapEdits, background_id: backgroundID }));
      }
    },
    [backgrounds, layoutData]
  );

  const updatePhrase = useCallback(async (eventID, index) => {
    // update which phrase option is selected
    setTextpathOptions((textpathOptions) => {
      const textpathOptionsCopy = { ...textpathOptions };
      textpathOptionsCopy[activeLayoutRef.current][eventID].options.forEach((tpo, i) => {
        tpo.selected = i === index;
      });
      textpathOptionsRef.current = textpathOptionsCopy;
      return textpathOptionsCopy;
    });
    const selectedPhrase = textpathOptionsRef.current[activeLayoutRef.current][eventID].options[index];
    setUpdatedPhrase({ eventID, selectedPhrase });
    setPhraseOptions(null);

    setTapEdits((tapEdits) => {
      const tapEditsCopy = { ...tapEdits };
      tapEditsCopy.phrase_edits = { ...tapEditsCopy.phrase_edits, [eventID]: selectedPhrase.textContent };
      return tapEditsCopy;
    });
  }, []);

  const updateTitle = useCallback(
    async (newTitle) => {
      if (newTitle !== tapestryData.title) {
        setTapestryData((tapestryData) => {
          const dataCopy = { ...tapestryData };
          dataCopy.title = newTitle;
          return dataCopy;
        });
        // update the SVG title
        const tspan = tapestryContainer.current?.querySelector(".tap-title tspan");
        if (tspan) {
          setTitleText(tspan, newTitle);
        } else {
          // search for title without .tap-title class
          for (const rootChild of tapestryContainer.current.querySelector("svg").childNodes) {
            const childNodes = [...rootChild.childNodes];
            if (rootChild.nodeName === "g" && childNodes.every((child) => child.nodeName === "text")) {
              const titleNode = childNodes.find(
                (child) =>
                  AUTO_TITLE_DATE_REGEX.test(child.querySelector("tspan")?.textContent) ||
                  child.querySelector("tspan")?.hasAttribute(AUTO_DATE_ATTR)
              );
              if (titleNode) {
                setTitleText(titleNode.querySelector("tspan"), newTitle);
              }
            }
          }
        }

        setTapEdits((tapEdits) => ({ ...tapEdits, title: newTitle }));
      }
    },
    [tapestryData]
  );

  const openEditTitle = useCallback(() => {
    setEditTitleValue(title || "");
    setEditTitleError(null);
    setEditTitleOpen(true);
  }, [title]);

  const submitEditTitle = useCallback(() => {
    const val = editTitleValue.trim();
    if (!val) {
      setEditTitleError("Please enter a title");
      return;
    }
    if (!/^[A-Za-z0-9\s'"_@./#&+-]*$/.test(val)) {
      setEditTitleError("Only letters and numbers allowed");
      return;
    }
    updateTitle(val);
    setTitle(val);
    setEditTitleOpen(false);
  }, [editTitleValue, updateTitle]);

  const saveTap = useCallback(() => {
    async function saveTapData() {
      const resp = await AppBackService.updateUserTap(userInfo.username, tapId, {
        tap_edits: { ...tapEdits, color: (backgrounds && backgrounds[activeBackground]) || "#000000" },
      });
      if (resp) {
        showSnackbar("Tapestry is saving. Please allow up to a minute for the update!", "success");
        setTapEdits({});
        markupDataCache.current = JSON.stringify(markupData);
        titleCache.current = title;
        tapDataCache.current = JSON.stringify(textpathOptions);
      } else {
        showSnackbar("Error saving Tapestry", "error");
      }
    }
    saveTapData();
  }, [markupData, showSnackbar, tapEdits, tapId, textpathOptions, title, userInfo, backgrounds, activeBackground]);

  const updateMarkupData = useCallback((newMarkupData) => {
    setMarkupData(newMarkupData);
    setTapEdits((tapEdits) => ({ ...tapEdits, markups: newMarkupData }));
  }, []);

  // Inject action buttons into navbar
  useEffect(() => {
    if (loading) return;
    setHeaderContent(
      <div className={s.navHeaderContent}>
        <span className={s.navTitle} title={title} onClick={openEditTitle}>{title} <EditIcon className={s.navTitleEdit} /></span>
        {tapDirty && (
          <button className={s.navBtnSave} onClick={saveTap}>
            Save Changes
          </button>
        )}
        <div className={s.navActions}>
          <button
            className={editingTapestry ? s.navBtnActive : s.navBtn}
            onClick={() => {
              setSearchingPhrases(false);
              setEditingTapestry((v) => !v);
            }}>
            <EditIcon /> Edit
          </button>
          {doc.filteredContent && (
            <button
              className={searchingPhrases ? s.navBtnActive : s.navBtn}
              onClick={() => {
                setEditingTapestry(false);
                setSearchingPhrases((v) => !v);
              }}>
              <InfoIcon /> Info
            </button>
          )}
          <button className={s.navBtnDanger} onClick={deleteTap}>
            <DeleteOutlineIcon /> Delete
          </button>
          <button className={s.navBtn} onClick={handleModalOpen}>
            <ShareIcon /> Share
          </button>
          {/* Inside a Zoom meeting (TT-1207 F4): show this Tapestry to everyone
              in it through Zoom's app share. Renders nothing elsewhere. */}
          <ZoomShareButton className={s.navBtn} onError={(message) => showSnackbar(message, "error")} />
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [loading, title, editingTapestry, searchingPhrases, doc.filteredContent, tapDirty, deleteTap, handleModalOpen, saveTap, setHeaderContent, openEditTitle, showSnackbar]);

  // Side panel delayed unmount for exit animation
  const panelOpen = searchingPhrases || editingTapestry;
  useEffect(() => {
    clearTimeout(sidePanelTimerRef.current);
    if (panelOpen) {
      setShowPanelContent(true);
      setLastPanelMode(searchingPhrases ? "transcript" : "markups");
    } else {
      sidePanelTimerRef.current = setTimeout(() => setShowPanelContent(false), 400);
    }
    return () => clearTimeout(sidePanelTimerRef.current);
  }, [panelOpen, searchingPhrases]);

  // drag markups from edit panel to tapestry

  const dummyMarkupStyle = useRef();
  const markupOffset = useRef({ x: 0, y: 0 });
  const dragMarkup = useGesture({
    onDragStart: ({ event, xy: [x, y], args }) => {
      const bbox = event.target.getBoundingClientRect();
      markupOffset.current.x = bbox.x + bbox.width / 2 - x;
      markupOffset.current.y = bbox.y + bbox.height / 2 - y;
      setDraggingMarkup(args[0]);
      if (dummyMarkupStyle.current) {
        dummyMarkupStyle.current.style.left = x + markupOffset.current.x + "px";
        dummyMarkupStyle.current.style.top = y + markupOffset.current.y + "px";
      }
    },
    onDrag: ({ xy: [x, y] }) => {
      if (dummyMarkupStyle.current) {
        dummyMarkupStyle.current.style.left = x + markupOffset.current.x + "px";
        dummyMarkupStyle.current.style.top = y + markupOffset.current.y + "px";
      }
    },
    onDragEnd: ({ event, xy: [x, y] }) => {
      const tapDimensions = getTapestryDimensions();
      const tapestryBBox = tapDimensions.tapestryBBox;
      const tapScale = tapDimensions.scale;
      const tapX = x - tapestryBBox.left + markupOffset.current.x;
      const tapY = y - tapestryBBox.top + markupOffset.current.y;
      const el = event.target;
      if (
        tapX - el.clientWidth / 4 > 0 &&
        tapX + el.clientWidth / 4 < tapestryBBox?.width &&
        tapY - el.clientHeight / 4 > 0 &&
        tapY + el.clientHeight / 4 < tapestryBBox?.height
      ) {
        // add markup to markup data
        const markupGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
        markupGroup.innerHTML = draggingMarkup?.svg;
        const markupSvg = markupGroup.querySelector("svg");
        const viewBox = markupSvg.viewBox.baseVal;
        const scale = (defaultMarkupHeight / viewBox.height) * tapScale;
        const newMarkupData = [
          ...markupData,
          {
            id: draggingMarkup.id,
            x: tapX * tapScale - viewBox.width * 0.5,
            y: tapY * tapScale - viewBox.height * 0.5,
            xScale: scale,
            yScale: scale,
            rotate: 0,
            dimensions: {
              width: viewBox.width,
              height: viewBox.height,
            },
          },
        ];
        updateMarkupData(newMarkupData);
      }
      setDraggingMarkup(null);
    },
  });

  // elements

  const transcript = useMemo(
    () => (
      <Box className={`side-panel-container ${s.transcript}`} ref={transcriptContainerRef}>
        {description && backgrounds && activeBackground && (
          <SlideDown title={"Primary Symbol: " + toTitleCase(nameOf(backgrounds[activeBackground].primarySymbolId))}>
            <Editor editorState={description} readOnly={true} />
          </SlideDown>
        )}
        <Typography className={`transcript-heading ${s.transcriptHead}`}>
          Transcript
        </Typography>
        <DocumentText docContent={doc.filteredContent} selectedText={selectedText} />
      </Box>
    ),
    [activeBackground, backgrounds, description, doc, nameOf, selectedText]
  );

  const markupsEl = useMemo(
    () => (
      <Box className={`side-panel-container ${s.markups}`}>
        {markups.map((markup) => (
          <img
            key={markup.id}
            src={`https://vt-markups.s3.us-west-2.amazonaws.com/${markup.id}` + ".svg"} // for some reason my IDE breaks if I don't concat here
            {...dragMarkup(markup)}
            className={classNames("markup-option", { dragging: draggingMarkup })}
            alt={markup.name}
            draggable="false"
          />
        ))}
      </Box>
    ),
    [dragMarkup, draggingMarkup, markups]
  );

  const phraseOptionsEl = useMemo(
    () =>
      phraseOptions ? (
        <Box className={`phrase-select ${s.phraseOptionsStyle}`}>
          <Box className={`phrase-select-header ${s.phraseSelHeader}`}>
            <Typography className={s.phraseSelectStyle} variant="h3">
              Select phrase
            </Typography>
            <CloseIcon className={s.closeIcon} onClick={hidePhraseOptions} />
          </Box>
          <Box className={`phrase-options ${s.phraseOptionsList}`}>
            {isSelectorValid("#phrase-" + phraseOptions.eventID) ? (
              phraseOptions.options.slice(0, NUM_PHRASE_OPTIONS).map((phraseOption, i) => (
                <Box
                  key={uuid()}
                  className={classNames(s.phraseOptionsListItem, { [s.selected]: phraseOption.selected })}
                  onClick={() => updatePhrase(phraseOptions.eventID, i)}>
                  {phraseOption.textContent}
                </Box>
              ))
            ) : (
              <Box className={s.noPhraseOptions}>No alternative phrases found</Box>
            )}
          </Box>
        </Box>
      ) : null,
    [hidePhraseOptions, phraseOptions, updatePhrase]
  );

  const tapestry = useMemo(
    () => (
      <Box
        className={classNames("edit-tapestry", s.tapestryMainPage, {
          [s.unEditable]: !editingTapestry && !searchingPhrases,
          "un-editable": !editingTapestry && !searchingPhrases,
          "editing-phrases": editingTapestry,
          "searching-phrases": searchingPhrases,
        })}
        style={{ paddingBottom: editingTapestry ? "40px" : "80px" }}>
        <Box className={`edit-tapestry-container ${s.viewTranscriptMedia}`}>
          <Box
            className={classNames("edit-tapestry-container", s.tapestryContainerStyles, {
              "view-transcript": searchingPhrases || editingTapestry,
            })}
            style={{
              height: (searchingPhrases || editingTapestry) && window.innerWidth > breakpoint ? tapestryHeight : "auto",
            }}
            ref={tapestryContainer}>
            {!svg[activeLayout] || loading ? (
              <CircularProgress size={60} className={s.circularProgress} />
            ) : (
              <TapestryView
                svg={svg[activeLayout]}
                updatedPhrase={updatedPhrase}
                width={(searchingPhrases || editingTapestry) && window.innerWidth > breakpoint ? 0.75 : 1}
                markupData={markupData}
                setMarkupData={updateMarkupData}
                editingTapestry={editingTapestry}
                colors={backgrounds && backgrounds[activeBackground]?.colors}
              />
            )}
            <div className={classNames(s.sidePanel, { [s.sidePanelOpen]: panelOpen })}>
              <div className={classNames(s.sidePanelInner, { [s.sidePanelInnerOpen]: panelOpen })}>
                {showPanelContent && lastPanelMode === "transcript" && doc.filteredContent && transcript}
                {showPanelContent && lastPanelMode === "markups" && markupsEl}
              </div>
            </div>
            {phraseOptionsEl}
          </Box>
        </Box>
      </Box>
    ),
    [
      editingTapestry,
      searchingPhrases,
      loading,
      title,
      updateTitle,
      tapestryData.meetingDate,
      tapDirty,
      saveTap,
      doc.filteredContent,
      deleteTap,
      handleModalOpen,
      tapestryHeight,
      svg,
      activeLayout,
      updatedPhrase,
      markupData,
      updateMarkupData,
      backgrounds,
      activeBackground,
      transcript,
      markupsEl,
      phraseOptionsEl,
      panelOpen,
      showPanelContent,
      lastPanelMode,
    ]
  );

  const panelSkeleton = useMemo(
    () =>
      Array.from(new Array(3)).map((_d, i) => (
        <Skeleton key={i} variant="rectangular" width={190} height={120} className={s.thumbnail} />
      )),
    []
  );

  // alternative backgrounds and layouts
  const editPanelElement = useMemo(
    () => (
      <Box className={`edit-panel ${s.editPanelStyles}`}>
        <Box className={s.editPanelStyle}>
          {backgrounds && Object.keys(backgrounds).length > 1 && (
            <Typography className={s.editPanelHeaderStyle}>Alternative Backgrounds</Typography>
          )}
          <Box className={s.editPanel}>
            {backgrounds ? (
              backgrounds.error ? (
                <ErrorIcon />
              ) : (
                Object.keys(backgrounds)
                  .filter((bgId) => bgId !== activeBackground)
                  .map((bgId) => (
                    <img
                      className={`edit-panel-option ${s.thumbnail}`}
                      key={bgId}
                      style={{ cursor: "pointer" }}
                      src={backgroundImageUrl(bgId)}
                      alt={backgrounds[bgId].description}
                      onClick={() => changeBackground(bgId)}
                      draggable="false"
                    />
                  ))
              )
            ) : (
              panelSkeleton
            )}
          </Box>
        </Box>
      </Box>
    ),
    [activeBackground, backgrounds, changeBackground, panelSkeleton]
  );

  const errorEl = useMemo(
    () => (
      <Box className={`error ${s.errorStyle}`}>
        <ErrorIcon />
      </Box>
    ),
    []
  );

  if (!tapId || redirect) {
    return <Navigate to="/" />;
  }
  if (error) {
    return errorEl;
  }
  return (
    <Box className={s.detailPage} id="detail-page-container">
      {tapestry}
      {editingTapestry && editPanelElement}
      {deleteTapConfirm}
      {snackbarEl}
      <Dialog fullWidth maxWidth="xs" open={editTitleOpen} onClose={() => setEditTitleOpen(false)}>
        <DialogTitle>Edit Tapestry Title</DialogTitle>
        <DialogContent>
          <DialogContentText>Enter a new title for this Tapestry.</DialogContentText>
          <TextField
            error={!!editTitleError}
            helperText={editTitleError}
            autoFocus
            margin="dense"
            type="text"
            fullWidth
            value={editTitleValue}
            onChange={(e) => setEditTitleValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitEditTitle(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTitleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitEditTitle}>Submit</Button>
        </DialogActions>
      </Dialog>
      <ShareModal open={modalOpen} handleClose={handleModalClose} tapShareUrl={imageURI} title={tapestryData.title} />
      <img
        className={s.dummyMarkup}
        style={{ display: draggingMarkup ? "block" : "none" }}
        ref={dummyMarkupStyle}
        src={`https://vt-markups.s3.us-west-2.amazonaws.com/${draggingMarkup?.id}` + ".svg"} // for some reason my IDE breaks if I don't concat here
        alt="Tapestry Markup"
        draggable="false"
      />
    </Box>
  );
};

function phrasesData(textpathOptions) {
  const data = {};
  for (const tpo of Object.values(textpathOptions)) {
    for (const option of tpo.options) {
      if (option.indexes) {
        data[option.textContent] = option.indexes;
      }
    }
  }
  return data;
}

function isSelectorValid(selector) {
  try {
    document.querySelector(selector);
    return true;
  } catch (e) {
    return false;
  }
}

export default DetailPage;
